import { KeyRound, Mic, Play, Settings, Square, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Brand } from "@/components/Brand.tsx";
import { ExportPanel } from "@/components/ExportPanel.tsx";
import { LanguageBar } from "@/components/LanguageBar.tsx";
import { Meter } from "@/components/Meter.tsx";
import { SessionPanel } from "@/components/SessionPanel.tsx";
import { ModelButton } from "@/components/ModelButton.tsx";
import { SettingsDialog } from "@/components/SettingsDialog.tsx";
import { SharePanel } from "@/components/SharePanel.tsx";
import { StatusPill } from "@/components/StatusPill.tsx";
import { TranscriptView } from "@/components/TranscriptView.tsx";
import { useBooth } from "@/hooks/useBooth.ts";
import { useEngineInfo } from "@/hooks/useEngineInfo.ts";
import { languageName } from "@/lib/languages.ts";
import { loadAccessCode, loadPrefs, saveAccessCode, savePrefs, type Prefs } from "@/lib/prefs.ts";
import { joinText } from "@/lib/protocol.ts";
import { ReadAloud } from "@/lib/speech.ts";
import { segments as orderedSegments } from "@/lib/transcript.ts";

export function BoothPage() {
  const [prefs, setPrefs] = useState<Prefs>(loadPrefs);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const { health, status, languages } = useEngineInfo();
  const [accessCode, setAccessCode] = useState(loadAccessCode);
  const [askCode, setAskCode] = useState(false);
  const booth = useBooth(prefs, accessCode);
  const codeRejected = booth.errorCode === "access_code";
  const showCodeForm = !booth.busy && (askCode || codeRejected);

  const startLive = () => {
    if (health?.accessCodeRequired && !accessCode) setAskCode(true);
    else void booth.start("live");
  };

  const submitCode = (code: string) => {
    saveAccessCode(code);
    setAccessCode(code);
    setAskCode(false);
    void booth.start("live", code);
  };
  const reader = useRef(new ReadAloud());

  const update = useCallback((patch: Partial<Prefs>) => {
    setPrefs((current) => {
      const next = { ...current, ...patch };
      if (next.source === next.target) next.target = current.source !== next.source ? current.source : next.target === "en" ? "fr" : "en";
      savePrefs(next);
      return next;
    });
  }, []);

  const segments = useMemo(() => orderedSegments(booth.transcript), [booth.transcript]);
  const plan = booth.session?.plan ?? { direction: prefs.direction, source: prefs.source, target: prefs.target };

  useEffect(() => reader.current.setEnabled(prefs.readAloud), [prefs.readAloud]);
  useEffect(() => {
    for (const seg of segments) {
      if (seg.final && seg.targetFinal) reader.current.offer(seg.id, joinText(seg.target), seg.targetLang);
    }
  }, [segments]);
  useEffect(() => {
    if (booth.phase === "connecting") reader.current.reset();
  }, [booth.phase]);

  const live = booth.phase === "live" || booth.phase === "reconnecting";
  const canStart = !booth.busy && status !== "down";

  const empty =
    booth.phase === "idle" ? (
      <div className="max-w-sm">
        <p className="font-display text-2xl italic text-fg">Speak, and read it in another language.</p>
        <p className="mt-2 text-sm">
          Words appear while you talk, settle as the model agrees with itself, and are finalized at each pause.
          Try the demo if no model is running.
        </p>
      </div>
    ) : booth.phase === "requesting-mic" ? (
      "Allow the microphone to begin."
    ) : live ? (
      "Listening. Start speaking."
    ) : booth.phase === "connecting" ? (
      "Connecting to the engine…"
    ) : (
      "Nothing was transcribed."
    );

  return (
    <div className="mx-auto flex h-dvh w-full max-w-7xl flex-col gap-3 px-4 py-3 sm:px-6 lg:gap-4 lg:py-6">
      <header className="flex items-center justify-between gap-3">
        <Brand subtitle="Live interpreter" />
        <div className="flex items-center gap-2">
          <StatusPill phase={booth.phase} />
          <ModelButton health={health} status={status} />
          <button type="button" className="btn h-10 w-10 px-0" onClick={() => setSettingsOpen(true)} aria-label="Settings">
            <Settings size={18} />
          </button>
        </div>
      </header>

      {booth.session?.simulated ? (
        <p className="rounded-xl border border-accent bg-accent-soft px-4 py-2 text-sm">
          Demo: a scripted conversation played through the real pipeline. No microphone and no model are involved.
        </p>
      ) : null}
      {booth.error ? (
        <p role="alert" className="rounded-xl border border-danger px-4 py-2 text-sm">
          {booth.error}
        </p>
      ) : null}
      {booth.notice ? <p className="rounded-xl border border-line bg-surface px-4 py-2 text-sm text-muted">{booth.notice}</p> : null}
      {status === "down" && !booth.busy ? (
        <p className="rounded-xl border border-danger px-4 py-2 text-sm">
          The speech engine is not reachable. Start it with <code className="font-mono">npm run engine</code>.
        </p>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto lg:grid lg:grid-cols-[minmax(0,1fr)_20rem] lg:overflow-visible">
        <main className="card flex h-full min-h-[24rem] shrink-0 flex-col overflow-hidden lg:min-h-0">
          <div className="border-b border-line p-4">
            <LanguageBar
              languages={languages}
              direction={prefs.direction}
              source={prefs.source}
              target={prefs.target}
              secondTarget={prefs.secondTarget === prefs.target || prefs.secondTarget === prefs.source ? "" : prefs.secondTarget}
              allowSecond={(health?.maxAttendeeLanguages ?? 2) > 1}
              disabled={booth.busy}
              onChange={update}
            />
          </div>

          <TranscriptView
            segments={segments}
            languages={languages}
            direction={plan.direction}
            speakerA={plan.source}
            layout={prefs.layout}
            showSource={prefs.showSource}
            textScale={prefs.textScale}
            live={live}
            empty={empty}
          />

          <div className="flex flex-col gap-3 border-t border-line p-4">
            {showCodeForm ? (
              <AccessCodeForm
                rejected={codeRejected}
                onSubmit={submitCode}
                onCancel={() => {
                  setAskCode(false);
                  if (codeRejected) booth.clear();
                }}
              />
            ) : null}
            <Meter level={booth.level} active={live && !booth.session?.simulated} />
            <div className="flex flex-wrap items-center gap-2">
              {booth.busy ? (
                <button
                  type="button"
                  className="btn min-h-12 flex-1 border-danger bg-danger px-6 text-base font-medium text-white sm:flex-none"
                  onClick={() => void booth.stop()}
                  disabled={booth.phase === "finalizing"}
                >
                  <Square size={18} fill="currentColor" /> {booth.phase === "finalizing" ? "Finishing…" : "Stop"}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn min-h-12 flex-1 border-accent bg-accent px-6 text-base font-medium text-accent-ink sm:flex-none"
                  onClick={startLive}
                  disabled={!canStart}
                >
                  <Mic size={18} /> Start
                </button>
              )}
              <button type="button" className="btn min-h-12" onClick={() => void booth.start("simulated")} disabled={!canStart}>
                <Play size={16} /> Demo
              </button>
              <button
                type="button"
                className="btn min-h-12 ms-auto"
                onClick={booth.clear}
                disabled={booth.busy || segments.length === 0}
                aria-label="Clear transcript"
              >
                <Trash2 size={16} /> <span className="hidden sm:inline">Clear</span>
              </button>
            </div>
          </div>
        </main>

        <aside className="flex flex-col gap-4 lg:min-h-0 lg:overflow-y-auto">
          <SharePanel
            code={booth.busy || booth.phase === "stopped" ? (booth.session?.code ?? null) : null}
            languages={languages}
            listenerLanguages={booth.session?.listenerLanguages ?? health?.listenerLanguages ?? []}
            voices={health?.voices ?? []}
          />
          <ExportPanel
            segments={segments}
            languages={languages}
            meta={{
              direction: plan.direction,
              languages: [languageName(languages, plan.source), languageName(languages, plan.target)],
              provider: booth.session?.provider,
              simulated: booth.session?.simulated ?? false,
            }}
          />
          <SessionPanel status={status} health={health} metrics={booth.metrics} session={booth.session} capture={booth.captureInfo} />
        </aside>
      </div>

      <SettingsDialog
        open={settingsOpen}
        prefs={prefs}
        busy={booth.busy}
        realtime={booth.session?.realtime ?? health?.aiEngine === "soniox"}
        onChange={(patch) => {
          update(patch);
          if (patch.listenerDelayS !== undefined) booth.setListenerDelay(patch.listenerDelayS);
        }}
        onClose={() => setSettingsOpen(false)}
      />
    </div>
  );
}

function AccessCodeForm({ rejected, onSubmit, onCancel }: { rejected: boolean; onSubmit: (code: string) => void; onCancel: () => void }) {
  const [code, setCode] = useState("");
  return (
    <form
      className="flex flex-col gap-2 rounded-xl border border-accent bg-accent-soft p-3 sm:flex-row sm:items-center"
      onSubmit={(e) => {
        e.preventDefault();
        if (code.trim()) onSubmit(code.trim());
      }}
    >
      <label htmlFor="access-code" className="flex items-center gap-2 text-sm">
        <KeyRound size={16} className="text-accent" />
        {rejected ? "That code was not accepted. Try again:" : "Live sessions need the access code:"}
      </label>
      <input
        id="access-code"
        type="password"
        autoComplete="current-password"
        className="field min-w-0 flex-1 text-base"
        value={code}
        onChange={(e) => setCode(e.target.value)}
        autoFocus
      />
      <div className="flex gap-2">
        <button type="submit" className="btn border-accent bg-accent text-accent-ink" disabled={!code.trim()}>
          Start
        </button>
        <button type="button" className="btn" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
